import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { BaseFilterComponent } from "@/components/filter/base-filter";
import { Breadcrumb, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableLoading } from "@/components/ui/table-body";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { MaintenanceView } from "@/views/common/maintenance.view";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

const LOADER_VARIANTS = Object.values(LoaderVariant).filter(
  (value): value is LoaderVariant => typeof value === "number",
);

const PREVIEW_SIZES = [LoaderSize.Large, LoaderSize.Medium, LoaderSize.Small];

const PENDING_QUERY_RESULT = { data: undefined, isPending: true, isError: false } as UseQueryResult<unknown, Error>;

const TABLE_COLUMNS = ["Name", "VIN", "Status", "Updated"];

const FILLED_BUTTON_COLORS = [
  { label: "Primary", colorVariant: ButtonColorVariant.Primary },
  { label: "Secondary", colorVariant: ButtonColorVariant.Secondary },
  { label: "Success", colorVariant: ButtonColorVariant.Success },
  { label: "Warning", colorVariant: ButtonColorVariant.Warning },
  { label: "Error", colorVariant: ButtonColorVariant.Error },
];

const noop = () => undefined;

function PreviewSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      {children}
    </section>
  );
}

interface UsagePreviewProps {
  title: string;
  usedIn: string[];
  children: ReactNode;
  className?: string;
}

function UsagePreview({ title, usedIn, children, className }: UsagePreviewProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription className="flex flex-col gap-0.5">
          {usedIn.map((file) => (
            <code key={file} className="text-xs">
              {file}
            </code>
          ))}
        </CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function VariantPreview({ variant }: { variant: LoaderVariant }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{LoaderVariant[variant]}</CardTitle>
        <CardDescription>
          <code className="text-xs">LoaderVariant.{LoaderVariant[variant]}</code>
          {variant === LoaderVariant.Road && " (default)"}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="bg-muted/30 flex h-40 items-center justify-center rounded-lg">
          <Loader variant={variant} animationClassName="h-28" />
        </div>
        <div className="flex items-end justify-between gap-4">
          {PREVIEW_SIZES.map((size) => (
            <div key={size} className="flex min-w-0 flex-col items-center gap-2">
              <div className="flex h-[100px] items-center justify-center">
                <Loader variant={variant} size={size} animationClassName="max-w-[160px]" />
              </div>
              <span className="text-muted-foreground text-xs">
                {LoaderSize[size]} ({size}px)
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function LoaderPreviewView() {
  return (
    <div className="flex flex-col gap-10 p-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Loader preview</h1>
        <p className="text-muted-foreground">
          All <code>Loader</code> variants and every place in the app that currently renders one, shown in its loading
          state.
        </p>
      </div>

      <PreviewSection
        title="Variants"
        description="Every LoaderVariant at a large preview size and at each LoaderSize."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3">
          {LOADER_VARIANTS.map((variant) => (
            <VariantPreview key={variant} variant={variant} />
          ))}
        </div>
      </PreviewSection>

      <PreviewSection
        title="Small · Wheel"
        description="Inline loaders inside buttons, inputs and text, all using LoaderSize.Small with LoaderVariant.Wheel."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <UsagePreview
            title="Filled button (inheritColor)"
            usedIn={[
              "components/ui/form-card.tsx",
              "components/pagination/page-input-popover.tsx",
              "components/merge/merge-dialog.tsx",
              "modules/fit/external/{load,unload}/….view.tsx",
              "modules/fab/settings/_components/….tsx",
            ]}
            className="lg:col-span-2"
          >
            <div className="flex flex-wrap items-center gap-3">
              {FILLED_BUTTON_COLORS.map(({ label, colorVariant }) => (
                <Button key={colorVariant} colorVariant={colorVariant} disabled>
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                  {label}
                </Button>
              ))}
              <Button size={ButtonSize.Icon} aria-label="Pending" disabled>
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
              </Button>
              <Button variant={ButtonVariant.Outlined} colorVariant={ButtonColorVariant.Secondary} disabled>
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                Outlined (default color)
              </Button>
            </div>
          </UsagePreview>

          <UsagePreview title="Filter button" usedIn={["components/filter/base-filter.tsx"]}>
            <BaseFilterComponent
              isLoading
              labelSingular="Brand"
              labelPlural="Brands"
              placeholder="Select brands"
              options={[]}
              selectedValues={[]}
              onSelectionChange={noop}
            />
          </UsagePreview>

          <UsagePreview
            title="Input duplicate check"
            usedIn={[
              "modules/masterdata/brand/{create,update}/…-form-fields.tsx",
              "modules/masterdata/company/{create,update}/…-form-fields.tsx",
              "modules/masterdata/{department,job-function,occupation}/…-form-fields.tsx",
              "modules/masterdata/{model-family,model-generation,vehicle}/…-form-fields.tsx",
              "modules/masterdata/user/{invite-guest,update-guest}/…-form-fields.tsx",
            ]}
          >
            <div className="relative max-w-sm">
              <Input placeholder="e.g. 12345" defaultValue="Mercedes-Benz" disabled />
              <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
              </div>
            </div>
          </UsagePreview>

          <UsagePreview title="Breadcrumb item" usedIn={["components/ui/breadcrumb.tsx"]}>
            <Breadcrumb>
              <BreadcrumbList>
                <li className="inline-flex items-center gap-1.5">
                  <BreadcrumbLink href="/loader-preview">Masterdata</BreadcrumbLink>
                </li>
                <BreadcrumbSeparator />
                <li className="inline-flex items-center gap-1.5">
                  <BreadcrumbLink href="/loader-preview">Vehicle</BreadcrumbLink>
                </li>
                <BreadcrumbSeparator />
                <li className="inline-flex items-center gap-1.5">
                  <BreadcrumbLink href="/loader-preview">
                    <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                  </BreadcrumbLink>
                </li>
              </BreadcrumbList>
            </Breadcrumb>
          </UsagePreview>

          <UsagePreview
            title="Load more"
            usedIn={["modules/fab/vehicle-valuation/confirm/_components/_components/vehicle-valuation-history.tsx"]}
          >
            <div className="flex justify-center py-1">
              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
            </div>
          </UsagePreview>
        </div>
      </PreviewSection>

      <PreviewSection title="Medium · Road" description="Content-area loaders using LoaderSize.Medium.">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <UsagePreview title="Table" usedIn={["components/ui/table-body.tsx"]} className="lg:col-span-2">
            <Table>
              <TableHeader>
                <TableRow>
                  {TABLE_COLUMNS.map((column) => (
                    <TableHead key={column}>{column}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableLoading columnsLength={TABLE_COLUMNS.length} />
              </TableBody>
            </Table>
          </UsagePreview>

          <UsagePreview
            title="Detail page"
            usedIn={[
              "components/uuid-existence-wrapper/uuid-existence-wrapper.tsx",
              "views/vehicle-detail/vehicle-vin-redirect.view.tsx",
            ]}
          >
            <UuidExistenceWrapper uuid="preview" query={() => PENDING_QUERY_RESULT} queryResult={PENDING_QUERY_RESULT}>
              {null}
            </UuidExistenceWrapper>
          </UsagePreview>

          <UsagePreview title="Avatar lightbox" usedIn={["components/user-avatar/avatar-lightbox-dialog.tsx"]}>
            <div className="flex justify-center rounded-lg bg-black/60 p-6 backdrop-blur-sm">
              <div className="bg-background/50 flex h-80 w-80 items-center justify-center rounded-xl">
                <Loader size={LoaderSize.Medium} />
              </div>
            </div>
          </UsagePreview>

          <UsagePreview
            title="Fullscreen in content area"
            usedIn={["modules/fab/vehicle-valuation/select/vehicle-valuation-select-dat-vehicle.view.tsx"]}
            className="lg:col-span-2"
          >
            <div className="h-80 rounded-lg border">
              <Loader size={LoaderSize.Medium} fullscreen isChild />
            </div>
          </UsagePreview>
        </div>
      </PreviewSection>

      <PreviewSection
        title="Large · Road"
        description="Page-level loaders using LoaderSize.Large. Fullscreen ones are framed here; in the app they fill the viewport."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <UsagePreview title="App start & route loading" usedIn={["app.tsx", "routes.tsx"]}>
            <div className="bg-background h-96 rounded-lg border">
              <Loader size={LoaderSize.Large} fullscreen isChild />
            </div>
          </UsagePreview>

          <UsagePreview
            title="Form page"
            usedIn={[
              "modules/ram/group/rule/rule-create.view.tsx",
              "modules/ram/group/rule/rule-uuid-update.view.tsx",
              "modules/ram/application/app-role/app-role-uuid-update.view.tsx",
              "modules/ram/application/app-role/attribute/attribute.view.tsx",
            ]}
          >
            <div className="rounded-lg border">
              <CenteredLayout variant={CenteredLayoutVariant.Default} title="Create rule" subTitle="Group rule">
                <div className="flex min-h-[40vh] items-center justify-center">
                  <Loader size={LoaderSize.Large} />
                </div>
              </CenteredLayout>
            </div>
          </UsagePreview>
        </div>
      </PreviewSection>

      <PreviewSection title="Lift" description="The maintenance view, using LoaderVariant.Lift.">
        <UsagePreview title="Maintenance view" usedIn={["views/common/maintenance.view.tsx"]}>
          <div className="h-[720px] overflow-hidden rounded-lg border [&>main]:min-h-full">
            <MaintenanceView />
          </div>
        </UsagePreview>
      </PreviewSection>
    </div>
  );
}
